import { AgendaDisplayOptions } from '../agenda';
import { SpeakerAgenda } from './speaker-agenda';
import { ProgramEvent } from '@cue/api';
import { Avatar, Button, Heading, Paragraph, Tooltip } from '@cue/atoms';
import { useQueryParams } from '@cue/hooks';
import { Modal, useModal } from '@cue/organisms';
import { StyledComponent } from '@cue/theme';
import { AnalyticsTransformer } from '@cue/utility';
import { CSSObject } from '@emotion/react';
import { LocationCategory } from '@project/cms/modules/agenda';
import React from 'react';
import slugify from 'slugify';

export type AgendaEventSpeakerCSSOverride = {
  '.cue-agenda-event-speaker-wrapper'?: CSSObject;
};

export const AgendaEventSpeakersPreview: React.FC<
  AgendaEventSpeakersProps &
    StyledComponent<AgendaEventSpeakerCSSOverride> & {
      agendaDisplayOptions?: AgendaDisplayOptions;
      locationCategories?: LocationCategory[];
      hideLocation?: boolean;
      disabled?: boolean;
      currentSpeaker?: NonNullable<ProgramEvent['persons']>[0];
      onSpeakerClick?: (speaker: NonNullable<ProgramEvent['persons']>[0]) => void;
    }
> = (props) => {
  const {
    persons,
    agendaDisplayOptions,
    locationCategories,
    hideLocation,
    disabled,
    currentSpeaker,
    onSpeakerClick,
  } = props;

  return (
    <div className="cue-agenda-event-avatar-wrapper">
      {persons
        ?.slice(0, 3)
        .map((speaker) => (
          <AgendaEventSpeaker
            speaker={speaker}
            key={speaker.id}
            agendaDisplayOptions={agendaDisplayOptions}
            locationCategories={locationCategories}
            hideLocation={hideLocation}
            disabled={disabled || currentSpeaker?.id === speaker.id}
            onSpeakerClick={onSpeakerClick}
          />
        ))}
    </div>
  );
};

/* eslint-disable sort-exports/sort-exports */
export const fallBackImage = `data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCA4MCA4MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayI+PGRlZnM+PHBhdGggaWQ9ImMiIGQ9Ik0wIDBoMzIuNTE2djMzLjk5N0gweiIvPjxwYXRoIGlkPSJlIiBkPSJNMCAwaDIyLjU4djI3LjlIMHoiLz48Y2lyY2xlIGlkPSJhIiBjeD0iNDAiIGN5PSI0MCIgcj0iNDAiLz48L2RlZnM+PGcgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIj48bWFzayBpZD0iYiIgZmlsbD0iI2ZmZiI+PHVzZSB4bGluazpocmVmPSIjYSIvPjwvbWFzaz48Y2lyY2xlIHN0cm9rZT0iI0ZGRiIgc3Ryb2tlLXdpZHRoPSIyIiBjeD0iNDAiIGN5PSI0MCIgcj0iNDEiLz48ZyBtYXNrPSJ1cmwoI2IpIj48cGF0aCBkPSJNMjkuMjU4IDQ3YzUuOTg2IDAgMTAuODM5LTQuODM2IDEwLjgzOS0xMC44cy00Ljg1My0xMC44LTEwLjgzOS0xMC44Yy01Ljk4NiAwLTEwLjgzOCA0LjgzNi0xMC44MzggMTAuOFMyMy4yNzIgNDcgMjkuMjYgNDciIGZpbGw9IiNGRkYiLz48ZyB0cmFuc2Zvcm09InRyYW5zbGF0ZSgxMyA0OS4wMDMpIj48bWFzayBpZD0iZCIgZmlsbD0iI2ZmZiI+PHVzZSB4bGluazpocmVmPSIjYyIvPjwvbWFzaz48Zy8+PHBhdGggZD0iTTMwLjI3IDEuMjA2IDIzLjI1OSAwYy0xLjU2MyAzLjE1OS00LjAwMSA1Ljg1NC03IDcuNjcyLTMuMDA4LTEuODE4LTUuNDQ2LTQuNTEzLTctNy42NzJMMi4yNTMgMS4yMDZBMi43MSAyLjcxIDAgMCAwIDAgMy44N3YzMC4xMjdoMzIuNTE2VjMuODdhMi43MDMgMi43MDMgMCAwIDAtMi4yNDUtMi42NjQiIGZpbGw9IiNGRkYiIG1hc2s9InVybCgjZCkiLz48L2c+PGcgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoNDYuNDIgMjApIj48bWFzayBpZD0iZiIgZmlsbD0iI2ZmZiI+PHVzZSB4bGluazpocmVmPSIjZSIvPjwvbWFzaz48Zy8+PHBhdGggZD0iTTE2LjI0OSAxOGMtLjkwMyAwLTEuNTcyLS42NjMtMS41NzItMS41NzUgMC0uOTIuNjc4LTEuNTgzIDEuNTktMS41NzUuODI4LjAwOCAxLjU0Ni43MTEgMS41NyAxLjUzNy4wMjkuOTIzLS42NDcgMS42MTMtMS41ODggMS42MTNabS00Ljk2OCAwYy0uOTAzIDAtMS41NzEtLjY2My0xLjU3MS0xLjU3NSAwLS45Mi42NzctMS41ODMgMS41ODgtMS41NzUuODMuMDA4IDEuNTQ3LjcxMSAxLjU3MiAxLjUzNy4wMjguOTIzLS42NDggMS42MTMtMS41ODkgMS42MTNabS00Ljk2NyAwYy0uOTA0IDAtMS41NzItLjY2My0xLjU3Mi0xLjU3NSAwLS45Mi42NzctMS41ODMgMS41ODktMS41NzUuODMuMDA4IDEuNTQ2LjcxMSAxLjU3MSAxLjUzN0M3LjkzIDE3LjMxIDcuMjU1IDE4IDYuMzE0IDE4Wk0wIDB2MjIuNWg1LjQzMWwtLjAxMiA1LjQgOC4xMy01LjRoOS4wMzJWMEgwWiIgZmlsbD0iI0ZGRiIgbWFzaz0idXJsKCNmKSIvPjwvZz48L2c+PC9nPjwvc3ZnPg==`;

export type AgendaEventSpeakersProps = Pick<ProgramEvent, 'persons'>;
type AgendaEventSpeakerProps = {
  speaker: Exclude<AgendaEventSpeakersProps['persons'], undefined>[0];
  agendaDisplayOptions?: AgendaDisplayOptions;
  locationCategories?: LocationCategory[];
  hideLocation?: boolean;
  disabled?: boolean;
  onSpeakerClick?: (speaker: NonNullable<ProgramEvent['persons']>[0]) => void;
};

const AgendaEventSpeaker: React.FC<AgendaEventSpeakerProps> = (props) => {
  const {
    speaker,
    agendaDisplayOptions,
    locationCategories,
    hideLocation,
    disabled,
    onSpeakerClick,
  } = props;
  const [modalRef] = useModal();
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const id = React.useId();
  const [params, setParams] = useQueryParams({ location });

  // currentSpeaker and speaker can be different if the user clicks on a different person in SpeakerAgenda (1) that has been opened by clicking on speaker (from props)
  // We cannot close this modal, because from a React point of view the new SpeakerAgenda (2) modal is a child of SpeakerAgenda (1) and closing SpeakerAgenda (1) would also remove the new SpeakerAgenda (2) modal.
  const [currentSpeaker, setCurrentSpeaker] =
    React.useState<NonNullable<ProgramEvent['persons']>[0]>(speaker);

  React.useEffect(() => {
    if (isModalOpen) {
      modalRef.current?.open();
    } else {
      modalRef.current?.close();
    }
  }, [isModalOpen, modalRef]);

  return (
    <React.Fragment key={id}>
      <div data-tooltip-id={id}>
        <Avatar
          overrideCSS={{
            '.cue-avatar-image': {
              cursor:
                agendaDisplayOptions?.speakerAgenda && !disabled ? 'pointer' : 'initial!important',
              '&:hover': {
                // transform: agendaDisplayOptions?.speakerAgenda && !disabled ? 'scale(1.1)' : 'none',
                boxShadow:
                  agendaDisplayOptions?.speakerAgenda && !disabled ? '0 0 0 1px #00e6dc' : 'none',
              },
            },
          }}
          speaker={{
            name: '',
            image: speaker.profileImageURL || fallBackImage,
          }}
          loading="lazy"
          {...(props.disabled
            ? {}
            : {
                onClick: () => {
                  if (agendaDisplayOptions?.speakerAgenda) {
                    if (onSpeakerClick) {
                      onSpeakerClick(speaker);
                      return;
                    }
                    modalRef.current?.open();
                    setParams({ ...params, speaker: slugify(speaker.name, { lower: true }) });
                  }

                  AnalyticsTransformer.trackEvent({
                    category: 'agenda',
                    type: 'speaker',
                    action: 'open',
                    data: { uid: speaker.id },
                  });
                },
              })}
        />
      </div>

      <Tooltip place="bottom" variant="light" id={id}>
        <Heading type="h5" className="cue-agenda-event-speaker-tooltip-name">
          {speaker.name}
        </Heading>
        {speaker.occupation ? (
          <Paragraph className="cue-agenda-event-speaker-tooltip-occ">
            {speaker.occupation} {speaker.company ? ` (${speaker.company})` : ''}
          </Paragraph>
        ) : null}
      </Tooltip>

      <Modal
        ref={modalRef}
        placement={{ vertical: 'top', horizontal: 'center' }}
        overrideCSS={{
          '.cue-modal-content': { width: '95vw', maxWidth: 1280, background: 'rgb(0, 24, 59)' },
          '.cue-modal-close-button-wrapper': {
            right: '0px',
            top: '0px',
            button: {
              borderRadius: '999px',
              width: '20px',
              height: '30px',
              '&:hover': {
                path: {
                  fill: '#000028',
                },
              },
            },
          },
        }}
        onVisibilityChange={(isOpen) => setIsModalOpen(isOpen)}
        customCloseRenderer={() => (
          <Button
            icon="close"
            styling="close"
            onClick={() => {
              setParams({ ...params, speaker: null });

              if (modalRef?.current?.animatedClose)
                modalRef.current.animatedClose({ className: 'modal-fade-out', duration: 500 });
            }}
          />
        )}>
        <SpeakerAgenda
          agendaDisplayOptions={agendaDisplayOptions}
          speaker={currentSpeaker}
          locationCategories={locationCategories}
          hideLocation={hideLocation}
          onSpeakerClick={(speaker) => {
            setCurrentSpeaker(speaker);
          }}
        />
      </Modal>
    </React.Fragment>
  );
};
